"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Fingerprint, KeyRound, ShieldCheck } from "lucide-react";

import { useSecurityMockContent } from "@/hooks/security/useSecurityMockContent";

import { SecuritySettingRow } from "./SecuritySettingRow";

const AUTH_ICONS = {
  mfa: ShieldCheck,
  passkey: Fingerprint,
  sso: KeyRound,
} as const;

const Toggle = ({ active }: { active: boolean }) => (
  <div
    className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${active ? "bg-zinc-800 dark:bg-white/80" : "bg-zinc-200 dark:bg-white/15"}`}
    aria-hidden
  >
    <span
      className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-transform dark:bg-[#14161c] ${active ? "left-[18px]" : "left-0.5"}`}
    />
  </div>
);

export const AuthVisual = () => {
  const prefersReducedMotion = useReducedMotion();
  const { authMethods } = useSecurityMockContent();

  return (
    <div className="flex w-full flex-col gap-1.5">
      {authMethods.map(({ id, title, status, detail, active }, index) => {
        const Icon = AUTH_ICONS[id];
        const statusVariant = active
          ? ("active" as const)
          : ("neutral" as const);

        return (
          <motion.div
            key={id}
            className="flex items-center"
            initial={{ opacity: 0, x: index % 2 === 0 ? -6 : 6 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            animate={
              active && !prefersReducedMotion ? { y: [0, -2, 0] } : undefined
            }
            transition={{
              delay: index * 0.08,
              ...(active && !prefersReducedMotion
                ? {
                    y: {
                      duration: 5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: index * 0.4,
                    },
                  }
                : {}),
            }}
          >
            <SecuritySettingRow
              icon={Icon}
              title={title}
              detail={detail}
              status={status}
              statusVariant={statusVariant}
              trailing={<Toggle active={active} />}
              className="w-full"
            />
          </motion.div>
        );
      })}
    </div>
  );
};
